/* eslint-disable complexity */
import React, { FC } from 'react';

import {
  AttachmentAction,
  AttachmentMode,
  AttachmentType,
} from '../../../../models/attachment';
import formatBytesToString from '../../../../utils/attachments/formatBytesToString';
import { getIsPwaIos } from '../../../../utils/webview/helpers';

import PreviewSegment from './PreviewSegment';
import * as Styled from './styled';
const ICON_SIZE = 20;

type Props = {
  actionDownload?: AttachmentAction;
  actionPreview?: AttachmentAction;
  attachment: AttachmentType;
  canDownloadFile: boolean;
  mode: AttachmentMode;
};

/**
 * Component that renders chip label for the attachment
 *
 * @param props                 Props passed to the component
 * @param props.actionDownload  Download action props
 * @param props.actionPreview   Preview action props
 * @param props.attachment      File that is attached
 * @param props.canDownloadFile Whether user has permission to download attachment
 * @param props.mode            In which mode to render the component
 * @returns                     The component itself
 */
const AttachmentLabel: FC<Props> = ({
  actionDownload,
  actionPreview,
  attachment,
  canDownloadFile,
  mode,
}) => {
  const { file, name } = attachment;
  /** @todo Remove check after 'size' is added in gql query for existing attachments */
  const fileSize = 'size' in file ? formatBytesToString(file.size) : '';

  // File is already attached to the message (feed, details view)
  if (actionDownload && actionPreview && mode !== 'compose') {
    const isPwaIos = getIsPwaIos();

    return (
      <Styled.LabelWrapper>
        <PreviewSegment actionPreview={actionPreview} mode={mode} name={name} />
        {canDownloadFile && mode === 'detailsEdit' && (
          <Styled.ButtonDownload
            color="var(--color-icons-primary)"
            iconSize={ICON_SIZE}
            isInProgress={actionDownload.isInProgress}
            link={actionDownload.link}
            linkRef={actionDownload.linkRef}
            requestButtonClick={actionDownload.requestButtonClick}
          />
        )}
        <Styled.HiddenLink
          aria-hidden="true"
          href={actionPreview.link}
          ref={actionPreview.linkRef}
          /**
           * Open the preview in the same tab on iOS PWA.
           * In Safari on iOS default setting is to block popups so preview would not open.
           */
          target={isPwaIos ? '_self' : '_blank'}
        />
      </Styled.LabelWrapper>
    );
  }

  // Attachment is in the draft state (compose form)
  return (
    <>
      <Styled.Title>{name}</Styled.Title>
      <Styled.Caption>{fileSize}</Styled.Caption>
    </>
  );
};

export default AttachmentLabel;
